import { useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Pencil, Trash2, ImageOff, Upload, Download } from "lucide-react";
import { toast } from "sonner";

const UOMS = ["kg", "gm", "piece", "pieces", "packet", "packets", "ltr", "ml", "dozen", "bunch"];

const empty = { name: "", name_ta: "", category_id: "", price: 0, uom: "kg", image_url: "", description: "", description_ta: "", is_active: true };

export default function ProductsPanel() {
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);

  const load = async () => {
    const [p, c] = await Promise.all([api.get("/products"), api.get("/categories")]);
    setItems(p.data || []);
    setCats(c.data || []);
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.name || !form.category_id || !form.uom || !form.price) {
      toast.error("Fill name, category, price, UoM");
      return;
    }
    try {
      const payload = { ...form, price: Number(form.price) };
      if (editingId) {
        await api.put(`/products/${editingId}`, payload);
        toast.success("Product updated");
      } else {
        await api.post("/products", payload);
        toast.success("Product added");
      }
      setOpen(false); setForm(empty); setEditingId(null);
      load();
    } catch (e) {
      toast.error(errMsg(e, "Save failed"));
    }
  };

  const edit = (p) => {
    setForm({
      name: p.name, name_ta: p.name_ta || "",
      category_id: p.category_id, price: p.price,
      uom: p.uom, image_url: p.image_url || "",
      description: p.description || "", description_ta: p.description_ta || "",
      is_active: p.is_active ?? true,
    });
    setEditingId(p.id);
    setOpen(true);
  };

  const del = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    await api.delete(`/products/${id}`);
    toast.success("Deleted"); load();
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-5">
        <div>
          <h2 className="text-xl font-semibold text-charcoal">Catalog</h2>
          <p className="text-sm text-charcoal/60">{items.length} products in the shop</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            data-testid="download-all-btn"
            onClick={async () => {
              try {
                const res = await api.get("/products/export.csv", { responseType: "blob" });
                const url = URL.createObjectURL(res.data);
                const a = document.createElement("a");
                a.href = url;
                const stamp = new Date().toISOString().slice(0, 10).replace(/-/g, "");
                a.download = `saravana-catalog-${stamp}.csv`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                URL.revokeObjectURL(url);
                toast.success("Catalog downloaded");
              } catch (err) {
                toast.error(errMsg(err, "Download failed"));
              }
            }}
          >
            <Download className="w-4 h-4 mr-1.5" /> Download all
          </Button>
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setForm(empty); setEditingId(null); } }}>
            <DialogTrigger asChild>
              <Button data-testid="add-product-btn" style={{ backgroundColor: "var(--forest)", color: "white" }}>
                <Plus className="w-4 h-4 mr-1.5" /> New product
              </Button>
            </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{editingId ? "Edit product" : "Add product"}</DialogTitle>
              <DialogDescription>Fill in the product details. Image can be uploaded or set via URL.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Name (English)</Label>
                  <Input data-testid="product-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                </div>
                <div>
                  <Label>பெயர் (Tamil, optional)</Label>
                  <Input data-testid="product-name-ta" value={form.name_ta} onChange={(e) => setForm({ ...form, name_ta: e.target.value })} placeholder="ஆப்பிள்" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Category</Label>
                  <Select value={form.category_id} onValueChange={(v) => setForm({ ...form, category_id: v })}>
                    <SelectTrigger data-testid="product-category"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      {cats.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Unit of measure</Label>
                  <Select value={form.uom} onValueChange={(v) => setForm({ ...form, uom: v })}>
                    <SelectTrigger data-testid="product-uom"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {UOMS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Price per {form.uom} (₹)</Label>
                <Input data-testid="product-price" type="number" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} />
              </div>
              <div>
                <Label>Product image</Label>
                <label
                  className="mt-1 flex flex-col items-center justify-center border-2 border-dashed border-black/15 rounded-md p-6 bg-stone/20 hover:bg-stone/40 hover:border-forest/40 transition-colors cursor-pointer relative"
                  data-testid="product-image-dropzone"
                >
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    data-testid="product-image-file"
                    onChange={async (e) => {
                      const f = e.target.files?.[0];
                      if (!f) return;
                      const fd = new FormData();
                      fd.append("file", f);
                      try {
                        const r = await api.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
                        const url = `${process.env.REACT_APP_BACKEND_URL}${r.data.url}`;
                        setForm((prev) => ({ ...prev, image_url: url }));
                        toast.success("Image uploaded");
                      } catch (err) {
                        toast.error(errMsg(err, "Upload failed"));
                      }
                    }}
                  />
                  {form.image_url ? (
                    <div className="w-full flex items-center gap-4">
                      <img src={form.image_url} alt="" className="w-24 h-24 rounded object-cover border border-black/10 shrink-0" />
                      <div className="flex-1 text-left">
                        <div className="text-sm text-charcoal font-medium">Image ready</div>
                        <div className="text-xs text-charcoal/60 truncate max-w-xs">{form.image_url}</div>
                        <div className="mt-2 flex gap-2">
                          <span className="text-xs text-forest underline">Replace</span>
                          <button
                            type="button"
                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); setForm({ ...form, image_url: "" }); }}
                            className="text-xs text-terracotta hover:underline"
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center">
                      <Upload className="w-8 h-8 mx-auto text-forest/60" />
                      <div className="mt-2 text-sm text-charcoal font-medium">Click to upload an image</div>
                      <div className="text-xs text-charcoal/50 mt-0.5">JPG, PNG, WEBP · up to 5 MB</div>
                    </div>
                  )}
                </label>
                <div className="mt-2">
                  <details className="text-xs">
                    <summary className="cursor-pointer text-charcoal/60 hover:text-forest">or paste an image URL</summary>
                    <Input
                      data-testid="product-image"
                      placeholder="https://..."
                      value={form.image_url}
                      onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                      className="mt-2"
                    />
                  </details>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Description (English)</Label>
                  <Textarea data-testid="product-description" rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
                </div>
                <div>
                  <Label>விவரம் (Tamil, optional)</Label>
                  <Textarea data-testid="product-description-ta" rows={2} value={form.description_ta} onChange={(e) => setForm({ ...form, description_ta: e.target.value })} placeholder="தமிழில் விவரம்" />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={save} data-testid="save-product-btn" style={{ backgroundColor: "var(--forest)", color: "white" }}>Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <div className="bg-white border border-black/10 rounded-md overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-16">Image</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>Price</TableHead>
              <TableHead>UoM</TableHead>
              <TableHead>Active</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.length === 0 && (
              <TableRow><TableCell colSpan={7} className="text-center text-charcoal/50 py-8">No products yet</TableCell></TableRow>
            )}
            {items.map((p) => (
              <TableRow key={p.id} data-testid={`product-row-${p.id}`}>
                <TableCell>
                  {p.image_url ? (
                    <img src={p.image_url} alt="" className="w-10 h-10 rounded object-cover" />
                  ) : (
                    <div className="w-10 h-10 rounded bg-stone flex items-center justify-center">
                      <ImageOff className="w-4 h-4 text-charcoal/40" />
                    </div>
                  )}
                </TableCell>
                <TableCell className="font-medium">{p.name}</TableCell>
                <TableCell>{p.category_name || "—"}</TableCell>
                <TableCell className="font-mono">₹{p.price}</TableCell>
                <TableCell>{p.uom}</TableCell>
                <TableCell>{p.is_active ? "Yes" : "No"}</TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="sm" onClick={() => edit(p)} data-testid={`edit-product-${p.id}`}>
                    <Pencil className="w-3.5 h-3.5" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => del(p.id)} data-testid={`delete-product-${p.id}`}>
                    <Trash2 className="w-3.5 h-3.5 text-terracotta" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
